import { useState } from 'react'
import styled from 'styled-components'
import { useDispatch } from 'react-redux'
import { Button, CrossIcon, H, P } from '@farewill/ui'
import { BORDER, COLOR, FONT, GTR } from '@farewill/ui/tokens'

import apiRequest from 'lib/axios/api-request'
import { hideModal } from 'state/actions'

type SendToExizentResponse = {
  data: {
    attributes: {
      jobId: string
      error: string | null
    }
  }
}

const PARTIALLY_SENT = [
  {
    title: 'Personal possessions and cash',
    description:
      'Vehicles are added, but personal items and cash amounts need adding manually.',
  },
  {
    title: 'Dependants',
    description: 'Added as people, but need marking as dependants manually.',
  },
]

const NOT_SENT = [
  ['State benefits', 'Utility providers', 'Funeral bill', 'Other bills'],
  ['Trusts', 'Power of attorney', 'Accountants or advisers'],
]

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${GTR.S};
  min-height: 100%;
`

const StatusBlock = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${GTR.XS};
`

const StatusHeading = styled.div`
  display: flex;
  align-items: center;
  gap: ${GTR.S};
`

const StatusIcon = styled.span<{ $background: string }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: ${GTR.L};
  height: ${GTR.L};
  border-radius: 50%;
  background-color: ${({ $background }) => $background};
`

// There is no minus icon
const Minus = styled.span`
  width: ${GTR.S};
  height: ${GTR.XXS};
  border-radius: ${BORDER.RADIUS.S};
  background-color: ${COLOR.GREY.DARK};
`

const StatusBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${GTR.S};
  padding-left: calc(${GTR.L} + ${GTR.S});
`

const ItemTitle = styled(P)`
  font-weight: ${FONT.WEIGHT.MEDIUM};
`

const NotSentColumns = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: ${GTR.L};
`

const NotSentList = styled.ul`
  list-style: disc;
  list-style-position: outside;
  margin: 0;
  padding-left: ${GTR.M};
  color: ${COLOR.GREY.DARK};
  font-size: ${FONT.SIZE.S};
  font-weight: ${FONT.WEIGHT.MEDIUM};
`

const ActionsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: auto;
`

type Props = {
  config: {
    probateCaseId: number
    onSent?: () => void
  }
}

const SendToExizentModal = ({ config: { probateCaseId, onSent } }: Props) => {
  const dispatch = useDispatch()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  const close = () => dispatch(hideModal())

  const handleConfirm = async () => {
    setSubmitError(null)
    setIsSubmitting(true)

    try {
      const response = await apiRequest({
        url: `/api/probate-cases/${probateCaseId}/send-to-exizent`,
        method: 'POST',
        silent: true,
      })

      const { attributes } = (response.data as SendToExizentResponse).data
      const queueError = attributes.error
      if (queueError) {
        setSubmitError(queueError)
        setIsSubmitting(false)
        return
      }

      setIsSubmitting(false)
      onSent?.()
      close()
    } catch {
      setSubmitError('Something went wrong. Please try again.')
      setIsSubmitting(false)
    }
  }

  return (
    <Content>
      <P color={COLOR.GREY.DARK} margin={0}>
        This will create the people and asset tiles with the information you’ve
        gathered. Some information won’t be sent, so you may need to add it
        manually.
      </P>

      <StatusBlock aria-labelledby="partially-sent">
        <StatusHeading>
          <StatusIcon $background={COLOR.ACCENT.PRIMARY_20} aria-hidden>
            <Minus />
          </StatusIcon>
          <H id="partially-sent" size="XS" tag="h3" margin={0}>
            Partially sent
          </H>
        </StatusHeading>
        <StatusBody>
          {PARTIALLY_SENT.map((item) => (
            <div key={item.title}>
              <ItemTitle color={COLOR.GREY.DARK} margin={[0, 0, 'XXS']}>
                {item.title}
              </ItemTitle>
              <P color={COLOR.GREY.DARK} margin={0}>
                {item.description}
              </P>
            </div>
          ))}
        </StatusBody>
      </StatusBlock>

      <StatusBlock aria-labelledby="not-sent">
        <StatusHeading>
          <StatusIcon $background={COLOR.GREY.LIGHT} aria-hidden>
            <CrossIcon width={GTR.S} height={GTR.S} color={COLOR.GREY.DARK} />
          </StatusIcon>
          <H id="not-sent" size="XS" tag="h3" margin={0}>
            Not sent
          </H>
        </StatusHeading>
        <StatusBody>
          <NotSentColumns>
            {NOT_SENT.map((column) => (
              <NotSentList key={column[0]}>
                {column.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </NotSentList>
            ))}
          </NotSentColumns>
        </StatusBody>
      </StatusBlock>

      <P color={COLOR.GREY.DARK} margin={0}>
        Note: This is a one-time sync. Any changes or new information should be
        added to the Exizent case directly.
      </P>

      {submitError && (
        <P color={COLOR.STATE.ERROR} margin={0}>
          {submitError}
        </P>
      )}

      <ActionsRow>
        <Button.Underline type="button" onClick={close} disabled={isSubmitting}>
          Cancel
        </Button.Underline>
        <Button.Primary
          type="button"
          onClick={handleConfirm}
          loading={isSubmitting}
        >
          Confirm
        </Button.Primary>
      </ActionsRow>
    </Content>
  )
}

export default SendToExizentModal
